import {Decimal} from 'decimal.js';

import {NumericString, QS_AMOUNT} from 'notebook/integrations/registry/MixtureGroupConstants';
import numDigitsForMixtureAmount from 'registry/mixtures/numDigitsForMixtureAmount';
import hasFiniteDecimalRepresentation from 'util/hasFiniteDecimalRepresentation';

export const MAX_MIXTURE_PRECISION = 30;

const TargetAmountDecimal = Decimal.clone({precision: MAX_MIXTURE_PRECISION});

/**
 * Return a string representation of the provided Decimal with the desired precision.
 *
 * Uses Decimal.toFixed instead of Decimal.toPrecision in order to ensure that the result is not in scientific
 * notation (in accordance with the wishes of mixtures customers).
 */
function decimalToPrecisionInFixedNotation(d: Decimal, desiredPrecision: number): string {
  const getExponent = (d: Decimal): number => Number(d.abs().log(10).floor());
  const currentExponent = getExponent(d);
  let numDecimalPlacesForDesiredPrecision = desiredPrecision - currentExponent - 1;
  // A negative value for the number of decimal places indicates the case where we need to use extra precision
  // in order to faithfully represent the value, due to the fact that we are not using scientific notation.
  numDecimalPlacesForDesiredPrecision = Math.max(numDecimalPlacesForDesiredPrecision, 0);
  return d.toFixed(numDecimalPlacesForDesiredPrecision);
}

// IMPORTANT: Keep in sync with benchling/registry/mixtures/compute_inherited_target_amount.py
export default function computeInheritedTargetAmount(
  parentIngredientAmount: NumericString,
  parentMixtureAmount: NumericString,
  mixtureAmount: NumericString
): string {
  if (parentIngredientAmount.stringValue === QS_AMOUNT) {
    return QS_AMOUNT;
  }

  // We care about trailing decimal 0s for the user-entered amount, but those are always ignored in the
  // decimal.js library, so here we use a custom precision function instead of Decimal.precision()
  const parentIngredientPrecision = numDigitsForMixtureAmount(parentIngredientAmount);

  const parentIngredientAmountDecimal = new TargetAmountDecimal(parentIngredientAmount.stringValue);
  const parentMixtureAmountDecimal = new TargetAmountDecimal(parentMixtureAmount.stringValue);
  const mixtureAmountDecimal = new TargetAmountDecimal(mixtureAmount.stringValue);
  const rawTargetAmount = parentIngredientAmountDecimal
    .times(mixtureAmountDecimal)
    .div(parentMixtureAmountDecimal);

  if (
    !hasFiniteDecimalRepresentation(
      parentIngredientAmountDecimal.times(mixtureAmountDecimal),
      parentMixtureAmountDecimal
    )
  ) {
    // Truncate to 3 decimal places by default, or more if either:
    //     - more digits are necessary to represent the value (i.e. for very small numbers)
    //     - the parent ingredient precision indicates there should be more
    const truncatedTo3DecimalPlaces = rawTargetAmount.toDecimalPlaces(3);
    if (
      !truncatedTo3DecimalPlaces.equals(0) &&
      truncatedTo3DecimalPlaces.precision() >= parentIngredientPrecision
    ) {
      return truncatedTo3DecimalPlaces.toString();
    } else {
      return decimalToPrecisionInFixedNotation(rawTargetAmount, parentIngredientPrecision);
    }
  } else {
    // Desired precision is whichever is larger between that of the parent ingredient and that of the
    // normalized (i.e. excluding trailing decimal 0s) raw target amount, without exceeding our max precision
    const desiredPrecision = Math.min(
      Math.max(parentIngredientPrecision, rawTargetAmount.precision()),
      MAX_MIXTURE_PRECISION
    );
    return decimalToPrecisionInFixedNotation(rawTargetAmount, desiredPrecision);
  }
}
